import { useEffect, useState } from 'react';
import { Mic, CheckCircle2, XCircle, Loader2, PhoneCall } from 'lucide-react';
import { toast } from 'sonner';
import { voiceOrders as voiceOrdersApi, API_BASE } from '../lib/api';

/**
 * Merchant Dashboard tab — inbox of voice-orders sent to this owner's shops.
 * Owner can play each recording in-app and mark it "Converted to Bill" once
 * they've entered it in POS / Khata.
 */
export default function OwnerVoiceOrders() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('new');
  const [busyId, setBusyId] = useState(null);

  const load = () => {
    setLoading(true);
    voiceOrdersApi.ownerList(filter ? { status: filter } : {})
      .then((d) => setRows(d.voice_orders || []))
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, [filter]);

  const setStatus = async (id, status) => {
    setBusyId(id);
    try {
      await voiceOrdersApi.updateStatus(id, { status });
      toast.success(status === 'converted' ? 'Marked as billed' : status === 'cancelled' ? 'Marked as cancelled' : 'Updated');
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not update');
    } finally { setBusyId(null); }
  };

  const audioSrc = (v) => {
    // Backend returns `/api/files/...` — /api is proxied by the ingress, so
    // relative URL works from the browser. For safety, prefix with API_BASE
    // when the path is relative but the app might be served from a different host.
    if (!v.audio_url) return null;
    if (v.audio_absolute_url) return v.audio_absolute_url;
    if (/^https?:\/\//.test(v.audio_url)) return v.audio_url;
    // API_BASE ends with `/api` — strip it since audio_url already begins with /api
    const origin = API_BASE.replace(/\/api\/?$/, '');
    return `${origin}${v.audio_url}`;
  };

  const STATUSES = [
    { id: 'new',       label: 'New',        cls: 'bg-[#0D3F12] text-white' },
    { id: 'converted', label: 'Converted',  cls: 'bg-[#1B5E20] text-white' },
    { id: 'cancelled', label: 'Cancelled',  cls: 'bg-ink text-white' },
    { id: '',          label: 'All',        cls: 'bg-ink/10 text-ink' },
  ];

  return (
    <div data-testid="owner-voice-orders">
      <div className="flex items-center gap-2 mb-3">
        <Mic className="w-4 h-4 text-[#0D3F12]"/>
        <div className="font-heading font-black text-lg">Voice orders inbox ({rows.length})</div>
      </div>
      <p className="text-xs text-ink/60 mb-3">
        Customers who couldn&apos;t type sent you these voice orders. Play, take the order into your POS, then mark <b>Converted to Bill</b>.
      </p>

      <div className="mb-4 flex gap-2 flex-wrap">
        {STATUSES.map((s) => (
          <button
            key={s.id || 'all'}
            data-testid={`voice-orders-filter-${s.id || 'all'}`}
            onClick={() => setFilter(s.id)}
            className={`inline-flex items-center rounded-full px-3 h-8 text-[11px] font-black uppercase tracking-wider transition ${
              filter === s.id ? s.cls : 'bg-white border-2 border-ink/15 text-ink/70 hover:border-ink'
            }`}
          >
            {s.label}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="text-ink/50 inline-flex items-center gap-2">
          <Loader2 className="w-4 h-4 animate-spin"/>Loading…
        </div>
      ) : rows.length === 0 ? (
        <div className="rounded-2xl border-2 border-dashed border-ink/20 p-8 text-center text-sm text-ink/60">
          No voice orders in this bucket yet.
        </div>
      ) : (
        <ul className="space-y-3">
          {rows.map((v) => (
            <li
              key={v.id}
              data-testid={`owner-voice-${v.id}`}
              className="rounded-2xl border-2 border-ink/10 bg-white p-4"
            >
              <div className="flex items-start gap-3 flex-wrap">
                <div className="w-10 h-10 rounded-lg bg-[#0D3F12] text-white grid place-items-center shrink-0">
                  <Mic className="w-5 h-5"/>
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-black text-ink truncate">{v.customer_name || 'Customer'}</span>
                    <span className="text-[10px] font-mono text-ink/50">
                      {v.customer_phone ? `+${v.customer_phone.replace(/\D+/g, '').slice(-10)}` : ''}
                    </span>
                    <span className={`ml-auto inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-black uppercase tracking-wider ${
                      v.status === 'new' ? 'bg-[#FFF3E0] text-[#B26900] border border-[#FFCC80]' :
                      v.status === 'converted' ? 'bg-[#E8F5E9] text-[#1B5E20] border border-[#A5D6A7]' :
                      'bg-[#ECEFF1] text-[#37474F] border border-[#B0BEC5]'
                    }`}>
                      {v.status === 'converted' && <CheckCircle2 className="w-3 h-3"/>}
                      {v.status === 'cancelled' && <XCircle className="w-3 h-3"/>}
                      {v.status}
                    </span>
                  </div>
                  <div className="text-[11px] text-ink/55 mt-0.5">
                    {v.store_name} · {new Date(v.created_at).toLocaleString()} ·{' '}
                    {v.duration_sec ? `${Math.round(v.duration_sec)}s` : `${Math.round((v.size_bytes || 0) / 1024)}kb`}
                  </div>
                  {v.note && <div className="mt-1 text-sm text-ink/80 italic">&ldquo;{v.note}&rdquo;</div>}
                </div>
              </div>

              <audio
                data-testid={`voice-audio-${v.id}`}
                src={audioSrc(v)}
                controls
                preload="none"
                className="w-full mt-3"
              />

              {/* AI transcript + parsed cart items (Whisper + Claude) */}
              {(v.transcript || (v.parsed_items && v.parsed_items.length) || v.parse_error) && (
                <div data-testid={`voice-transcript-${v.id}`} className="mt-3 rounded-lg bg-[#F1F8E9] border border-[#A5D6A7] p-3">
                  <div className="text-[10px] uppercase tracking-[0.2em] font-black text-[#1B5E20] mb-1">
                    AI Transcript {v.parse_confidence != null && <span className="ml-1 text-ink/50 font-mono">· {Math.round((v.parse_confidence || 0) * 100)}% confidence</span>}
                  </div>
                  {v.transcript && (
                    <div className="text-sm text-ink italic">&ldquo;{v.transcript}&rdquo;</div>
                  )}
                  {v.parsed_items && v.parsed_items.length > 0 && (
                    <ul className="mt-2 text-xs text-ink/85 grid grid-cols-1 md:grid-cols-2 gap-y-1">
                      {v.parsed_items.map((it, i) => (
                        <li key={i} className="flex items-center gap-1.5">
                          <span className="inline-block w-1.5 h-1.5 rounded-full bg-[#1B5E20]"/>
                          <span><b>{it.qty} {it.unit}</b> × {it.name}</span>
                        </li>
                      ))}
                    </ul>
                  )}
                  {v.parse_error && !v.transcript && (
                    <div className="text-[11px] text-[#B71C1C]">Transcription failed: {v.parse_error}</div>
                  )}
                </div>
              )}

              {v.status === 'new' && (
                <div className="mt-3 flex gap-2 flex-wrap">
                  <button
                    data-testid={`voice-convert-${v.id}`}
                    onClick={() => setStatus(v.id, 'converted')}
                    disabled={busyId === v.id}
                    className="inline-flex items-center gap-1.5 rounded-md bg-[#1B5E20] hover:bg-[#0D3F12] disabled:opacity-50 text-white font-black uppercase text-xs tracking-wider px-4 h-10"
                  >
                    <CheckCircle2 className="w-4 h-4"/>Converted to Bill
                  </button>
                  <button
                    data-testid={`voice-cancel-${v.id}`}
                    onClick={() => setStatus(v.id, 'cancelled')}
                    disabled={busyId === v.id}
                    className="inline-flex items-center gap-1.5 rounded-md border-2 border-ink/20 hover:border-ink text-ink font-black uppercase text-xs tracking-wider px-4 h-10"
                  >
                    Cancel
                  </button>
                  {v.customer_phone && (
                    <a
                      data-testid={`voice-call-${v.id}`}
                      href={`tel:${v.customer_phone}`}
                      className="inline-flex items-center gap-1.5 rounded-md border-2 border-[#0D47A1] text-[#0D47A1] font-black uppercase text-xs tracking-wider px-4 h-10"
                    >
                      <PhoneCall className="w-3.5 h-3.5"/>Call
                    </a>
                  )}
                </div>
              )}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
